// LIBRERIE/IMPORTS
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// COMPONENTI
import NotFoundProperty from '../Error/NotFoundProperty';

// CONFIGURAZIONI & TIPI
import { Property } from '../../types/property';
import appConfig from '../../config/appConfig';

// FOGLI DI STILE
import styles from './ImmobiliList.module.css';

interface ImmobiliListProps {
    properties: Property[];
    loading: boolean;
    error: any;
    basePath: string;
    labelSold: boolean;
}

const ImmobiliList: React.FC<ImmobiliListProps> = ({ properties, loading, error, basePath, labelSold }) => {
    const { t, i18n } = useTranslation();

    if (!loading && !error && properties.length === 0) {
        return <NotFoundProperty />;
    }

    const getPropertyName = (property: Property) => {
        return `${property.tipologyDetails.name}${t('property.inAddress', ' a ') as string}${property.addressDetails.quartiereName || property.addressDetails.frazioneName || property.addressDetails.comuneName}`;
    };

    const langPrefix = appConfig.integrations.multilang ? `/${i18n.language}` : '';

    return (
        <>
            {properties.map((property: Property) => {
                const propertyName = getPropertyName(property);
                const propertyUrl = `${langPrefix}/${basePath}/${property.id}`;
                return (
                    <div className={`md-6 ${styles.propertyListItem}`} key={property.id}>
                        <div className={styles.propertyImageContainer}>
                            <Link to={propertyUrl}>
                                <img
                                    className={styles.propertyImage}
                                    src={property.mainImage}
                                    alt={(t('property.card.imageAltBase', 'Immagine proprietà') as string) + ` ${propertyName}`}
                                />
                            </Link>
                            {labelSold ? (
                                <div className={styles.propertyLabel}>
                                    <span className={`badge ${styles.badgesuccessi}`}>
                                        VENDUTO DA NOI
                                    </span>
                                </div>
                            ) : (
                                <div className={styles.propertyLabel}>
                                    {property.contractDetails?.name && (
                                        <span className={`badge ${styles.badge}`}>
                                            {t(`property.contractTypes.${property.contractDetails.name}`, property.contractDetails.name) as string}
                                        </span>
                                    )}
                                </div>
                            )}
                            {property.exclusive && (
                                <div className={styles.exclusiveBox}>
                                    <span className={styles.exclusiveText}>{t('property.exclusive', 'Esclusiva') as string}</span>
                                </div>
                            )}
                        </div>

                        <div className={styles.propertyDetails}>
                            <h5 className={styles.propertyTitle}>
                                <a href={`/immobili/${property.id}`}>
                                    {(property.tipologyDetails.name + (t('property.inAddress', ' a ') as string) +
                                    (property.addressDetails.quartiereName || property.addressDetails.frazioneName || property.addressDetails.comuneName)) as string}
                                </a>
                            </h5>

                            <p className={styles.propertyAddress}>
                                <i className="fas fa-map-marker-alt"></i>
                                {property.addressDetails.frazioneName || property.addressDetails.quartiereName ?
                                    `${property.addressDetails.frazioneName || property.addressDetails.quartiereName}, ${property.addressDetails.comuneName} (${property.addressDetails.provinciaSigla ?? ""})`
                                    : property.addressDetails.comuneName ?
                                        `${property.addressDetails.comuneName} (${property.addressDetails.provinciaSigla ?? ""})`
                                        : property.addressDetails.provinciaName ?? ''
                                }
                            </p>

                            <ul className={styles.propertyInfo}>
                                {property.rooms != null && property.rooms !== '0' && (
                                    <li>
                                        <i className="fas fa-bed"></i>
                                        <span>{property.rooms} {(parseInt(String(property.rooms), 10) === 1 ? t('common.room', 'Camera') : t('common.rooms', 'Camere')) as string}</span>
                                    </li>
                                )}
                                {property.bathrooms != null && property.bathrooms !== 0 && (
                                    <li>
                                        <i className="fas fa-bath"></i>
                                        <span>{property.bathrooms} {(property.bathrooms === 1 ? t('common.bathroom', 'Bagno') : t('common.bathrooms', 'Bagni')) as string}</span>
                                    </li>
                                )}
                                {property.sqmt && property.sqmt !== 0 && (
                                    <li>
                                        <i className="fas fa-vector-square"></i>
                                        <span>{property.sqmt} m²</span>
                                    </li>
                                )}
                            </ul>

                            <div className={styles.propertyFooter}>
                                <div className={styles.propertyPrice}>
                                    {property.priceDetails.reservedNegotiation ? (
                                        <span className={styles.reserved}>{t('property.priceReserved', 'Tr. Riservata') as string}</span>
                                    ) : (
                                        <>
                                            € {new Intl.NumberFormat('it-IT').format(property.priceDetails.price)}
                                            {property.priceDetails.negotiable && (
                                                <span className={styles.negotiable}>{`(${(t('common.negotiable', 'Tr.') as string)})`}</span>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className={styles.propertyActions}>
                                    <a
                                        className={styles.detailsButton}
                                        href={`/immobili/${property.id}`}
                                        aria-label={t('property.card.ariaViewDetails', `Visualizza dettagli per ${propertyName}`, { propertyName: propertyName }) as string}
                                    >
                                        {t('common.details', 'Dettagli') as string}
                                    </a>
                                    <div className={styles.propertyReference}>
                                        <span className={styles.refLabel}>{(t('property.reference', 'Rif.') as string)}:</span> {property.ref}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </>
    );
};

export default ImmobiliList;